import React, { useState, useEffect } from 'react';
import {
  Stethoscope,
  Pill,
  HeartPulse,
  Microscope,
  Bone,
  Brain,
  Eye,
  Tooth,
  Baby,
  Calendar,
  Clock,
  Phone,
  MapPin,
  Mail,
  ChevronRight,
  ArrowRight,
  Star,
  Check,
  Shield,
  Users,
  Award,
  Activity,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Menu,
  X,
  Search,
  User,
  ChevronDown,
  Play,
  Quote,
  BadgeCheck,
  Zap,
  Globe,
  TabletSmartphone,
  ClipboardCheck
} from 'lucide-react';

// Mock images (in a real app, these would be imported)
const doctor1 = 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80';
const doctor2 = 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80';
const doctor3 = 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1124&q=80';
const clinicBuilding = 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80';
const waitingRoom = 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80';
const labEquipment = 'https://images.unsplash.com/photo-1581093450021-4a7360e9a7a6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80';

const EnterpriseClinicHomepage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeServiceTab, setActiveServiceTab] = useState('all');
  const [appointmentForm, setAppointmentForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAppointmentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const services = [
    { id: 1, name: 'General Medicine', icon: Stethoscope, category: 'general', featured: true, description: 'Comprehensive primary care for all ages with personalized treatment plans.' },
    { id: 2, name: 'Cardiology', icon: HeartPulse, category: 'specialty', featured: true, description: 'Advanced heart care with state-of-the-art diagnostic technology.' },
    { id: 3, name: 'Pediatrics', icon: Baby, category: 'specialty', featured: true, description: 'Specialized care for infants, children, and adolescents.' },
    { id: 4, name: 'Dentistry', icon: Star, category: 'specialty', description: 'Complete oral health services from routine cleanings to complex procedures.' },
    { id: 5, name: 'Ophthalmology', icon: Eye, category: 'specialty', description: 'Expert eye care including vision correction and disease treatment.' },
    { id: 6, name: 'Orthopedics', icon: Bone, category: 'specialty', description: 'Musculoskeletal care with both surgical and non-surgical options.' },
    { id: 7, name: 'Neurology', icon: Brain, category: 'specialty', description: 'Diagnosis and treatment of complex nervous system disorders.' },
    { id: 8, name: 'Laboratory Services', icon: Microscope, category: 'diagnostic', description: 'Comprehensive lab testing with rapid, accurate results.' },
    { id: 9, name: 'Pharmacy', icon: Pill, category: 'support', description: 'Full-service pharmacy with medication counseling available.' },
  ];

  const testimonials = [
    { id: 1, name: 'Sarah Johnson', role: 'Cardiology Patient', content: 'The care I received was exceptional. The doctors took time to listen and explain everything clearly. The facilities are modern and comfortable.', rating: 5, image: 'https://randomuser.me/api/portraits/women/65.jpg' },
    { id: 2, name: 'Michael Chen', role: 'Pediatric Care', content: 'Clean facility with modern equipment. The staff was friendly and professional. My children actually look forward to their check-ups!', rating: 4, image: 'https://randomuser.me/api/portraits/men/32.jpg' },
    { id: 3, name: 'Emma Davis', role: 'Senior Care', content: 'They treated my elderly mother with such compassion. The geriatric specialists are truly knowledgeable and patient.', rating: 5, image: 'https://randomuser.me/api/portraits/women/44.jpg' },
  ];

  const doctors = [
    { id: 1, name: 'Dr. Robert Smith', specialty: 'Cardiologist', experience: '15 years', image: doctor1, bio: 'Board-certified cardiologist with expertise in interventional procedures.' },
    { id: 2, name: 'Dr. Jennifer Williams', specialty: 'Pediatrician', experience: '12 years', image: doctor2, bio: 'Specializes in childhood development and preventive care.' },
    { id: 3, name: 'Dr. David Johnson', specialty: 'Neurologist', experience: '18 years', image: doctor3, bio: 'Expert in treating complex neurological disorders.' },
  ];

  const stats = [
    { value: '25,000+', label: 'Patients Treated', icon: Users, description: 'Serving our community with excellence' },
    { value: '50+', label: 'Specialized Doctors', icon: Stethoscope, description: 'Board-certified physicians' },
    { value: '15', label: 'Years of Experience', icon: Award, description: 'Trusted healthcare provider' },
    { value: '98%', label: 'Patient Satisfaction', icon: Activity, description: 'Highest quality care' },
  ];

  const accreditations = [
    { name: 'Joint Commission Accredited', icon: BadgeCheck },
    { name: 'CMS 5-Star Rating', icon: Star },
    { name: 'Healthgrades Excellence Award', icon: Award },
  ];

  const filteredServices = activeServiceTab === 'all' 
    ? services 
    : services.filter(service => service.category === activeServiceTab);

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      {/* Header - Sticky with scroll effect */}
      <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <HeartPulse className="w-6 h-6 text-white" />
              </div>
              <span className={`text-2xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>MediCare+</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="#" className={`font-medium ${scrolled ? 'text-blue-600' : 'text-white'}`}>Home</a>
              <a href="#" className={`hover:text-blue-600 transition ${scrolled ? 'text-gray-700' : 'text-white'}`}>Services</a>
              <a href="#" className={`hover:text-blue-600 transition ${scrolled ? 'text-gray-700' : 'text-white'}`}>Doctors</a>
              <a href="#" className={`hover:text-blue-600 transition ${scrolled ? 'text-gray-700' : 'text-white'}`}>About</a>
              <a href="#" className={`hover:text-blue-600 transition ${scrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
              <div className="relative group">
                <button className={`flex items-center ${scrolled ? 'text-gray-700' : 'text-white'} hover:text-blue-600`}>
                  More <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600">Telehealth</a>
                  <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600">Research</a>
                  <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600">Medical Education</a>
                </div>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSearchOpen(!searchOpen)}
                className={`p-2 ${scrolled ? 'text-gray-600' : 'text-white'} hover:text-blue-600`}
              >
                <Search className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-blue-600"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <button className={`hidden lg:flex items-center space-x-2 ${scrolled ? 'bg-blue-600' : 'bg-white'} hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition`}>
                <User className="w-4 h-4" />
                <span>Patient Portal</span>
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          {searchOpen && (
            <div className="mt-3 lg:hidden">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search services, doctors..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <div className="container mx-auto px-4 py-3 space-y-3">
              <a href="#" className="block py-2 px-3 bg-blue-50 text-blue-600 rounded-lg">Home</a>
              <a href="#" className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-lg">Services</a>
              <a href="#" className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-lg">Doctors</a>
              <a href="#" className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-lg">About</a>
              <a href="#" className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-lg">Contact</a>
              <div className="pt-2 border-t border-gray-200">
                <button className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition">
                  <User className="w-4 h-4" />
                  <span>Patient Portal</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section with Background Image */}
        <section className="relative h-screen min-h-[800px] flex items-center">
          <div className="absolute inset-0 bg-blue-900 opacity-75"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center z-0" 
            style={{ backgroundImage: `url(${clinicBuilding})` }}
          ></div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-2xl text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                World-Class <span className="text-blue-300">Healthcare</span> Excellence
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8">
                Advanced medical care with compassion. Our JCI-accredited facility brings together cutting-edge technology and renowned specialists.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg flex items-center justify-center space-x-2 transition duration-300 transform hover:scale-105">
                  <Calendar className="w-5 h-5" />
                  <span>Book Appointment</span>
                </button>
                <button 
                  onClick={() => setVideoModalOpen(true)}
                  className="bg-transparent border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-bold text-lg flex items-center justify-center space-x-2 transition duration-300 transform hover:scale-105"
                >
                  <Play className="w-5 h-5" />
                  <span>Watch Video</span>
                </button>
              </div>
            </div>
          </div>

          {/* Stats Banner Floating Over Hero */}
          <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2">
            <div className="container mx-auto px-4">
              <div className="bg-white rounded-xl shadow-2xl p-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
                {stats.map((stat, index) => (
                  <div key={index} className="py-2 px-4 flex items-center justify-center">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-blue-100 rounded-full">
                        <stat.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                        <p className="text-xs text-gray-500">{stat.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {videoModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-4xl">
              <button 
                onClick={() => setVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="aspect-w-16 aspect-h-9 bg-black">
                <iframe 
                  className="w-full h-[500px]"
                  src="https://www.youtube.com/embed/7S7r7UZaLiY?autoplay=1" 
                  title="Clinic Tour" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}

        {/* Services Section */}
        <section className="py-28 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold mb-2 inline-block">OUR SERVICES</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Comprehensive Medical Care</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From routine check-ups to complex procedures, we offer a full spectrum of healthcare services.
              </p>
            </div>

            {/* Service Tabs */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-white rounded-lg shadow-sm p-1 border border-gray-200">
                <button
                  onClick={() => setActiveServiceTab('all')}
                  className={`px-6 py-3 text-sm font-medium rounded-md ${activeServiceTab === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:text-blue-600'}`}
                >
                  All Services
                </button>
                <button
                  onClick={() => setActiveServiceTab('general')}
                  className={`px-6 py-3 text-sm font-medium rounded-md ${activeServiceTab === 'general' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:text-blue-600'}`}
                >
                  Primary Care
                </button>
                <button
                  onClick={() => setActiveServiceTab('specialty')}
                  className={`px-6 py-3 text-sm font-medium rounded-md ${activeServiceTab === 'specialty' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:text-blue-600'}`}
                >
                  Specialty Care
                </button>
                <button
                  onClick={() => setActiveServiceTab('diagnostic')}
                  className={`px-6 py-3 text-sm font-medium rounded-md ${activeServiceTab === 'diagnostic' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:text-blue-600'}`}
                >
                  Diagnostics
                </button>
              </div>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service) => (
                <div 
                  key={service.id} 
                  className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="p-8">
                    <div className="flex items-start space-x-5">
                      <div className="p-4 bg-blue-100 rounded-xl">
                        <service.icon className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{service.name}</h3>
                        <p className="text-gray-600 mb-5">
                          {service.description}
                        </p>
                        <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center group">
                          Learn more 
                          <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {service.featured && (
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-3 text-sm text-white flex items-center">
                      <Zap className="w-4 h-4 mr-2" />
                      <span>Featured Service</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section with Image */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 mb-12 lg:mb-0 lg:pr-12">
                <span className="text-blue-600 font-semibold mb-2 inline-block">ABOUT US</span>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">A Legacy of Medical Excellence</h2>
                <p className="text-lg text-gray-600 mb-6">
                  Founded in 2005, MediCare+ has grown to become a regional leader in healthcare delivery, combining academic medicine with community care.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start space-x-4">
                    <Check className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">JCI Accredited</h4>
                      <p className="text-gray-600">Meeting the highest international healthcare standards</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Globe className="w-6 h-6 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Global Network</h4>
                      <p className="text-gray-600">Partnerships with leading medical institutions worldwide</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <TabletSmartphone className="w-6 h-6 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Digital Health</h4>
                      <p className="text-gray-600">State-of-the-art electronic medical records and telehealth</p>
                    </div>
                  </div>
                </div>
                <button className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
                  Our Story
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
              <div className="lg:w-1/2 relative">
                <div className="rounded-xl overflow-hidden shadow-2xl">
                  <img 
                    src={waitingRoom} 
                    alt="Clinic interior" 
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg w-3/4">
                  <div className="flex items-center space-x-3">
                    <ClipboardCheck className="w-10 h-10 text-blue-600" />
                    <div>
                      <p className="text-xl font-bold text-gray-900">15+</p>
                      <p className="text-gray-600">Specialized Departments</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Doctors Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold mb-2 inline-block">OUR TEAM</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Meet Our Specialist Physicians</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Board-certified doctors with extensive experience in their respective fields.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {doctors.map((doctor) => (
                <div key={doctor.id} className="group">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-96">
                    <img 
                      src={doctor.image} 
                      alt={doctor.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-6">
                      <h3 className="text-xl font-bold text-white mb-1">{doctor.name}</h3>
                      <p className="text-blue-300 font-medium mb-3">{doctor.specialty}</p>
                      <p className="text-gray-300 text-sm mb-4">{doctor.bio}</p>
                      <button className="self-start bg-white text-blue-600 hover:bg-gray-100 px-4 py-2 rounded-lg font-medium text-sm transition">
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <button className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
                Meet All Doctors
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold mb-2 inline-block">TESTIMONIALS</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">What Our Patients Say</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Don't just take our word for it - hear from those we've cared for.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-blue-100 mb-4" />
                  <p className="text-gray-700 text-lg mb-6">"{testimonial.content}"</p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden mr-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Accreditation Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-6">Our Accreditations & Awards</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Recognized for excellence in patient care, safety, and innovation.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {accreditations.map((item, index) => (
                <div key={index} className="bg-gray-800 p-8 rounded-xl flex flex-col items-center text-center">
                  <item.icon className="w-12 h-12 text-blue-400 mb-4" />
                  <h3 className="text-xl font-bold mb-2">{item.name}</h3>
                  <p className="text-gray-400">
                    {index === 0 && 'Gold Seal of Approval for meeting international standards'}
                    {index === 1 && 'Top rating from Centers for Medicare & Medicaid Services'}
                    {index === 2 && 'Recognized for outstanding patient outcomes'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Appointment Section */}
        <section className="py-20 bg-blue-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-500"></div>
          </div>
          <div className="container mx-auto px-4 lg:px-8 relative">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/2 p-12 bg-blue-600">
                  <h2 className="text-3xl font-bold mb-4">Ready to Schedule Your Visit?</h2>
                  <p className="text-blue-100 text-lg mb-8">
                    Our patient care coordinators are standing by to help you book your appointment with the right specialist.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <Phone className="w-6 h-6 text-blue-300 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-lg">Call Us</h4>
                        <p className="text-blue-200">(555) 123-4567</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <Mail className="w-6 h-6 text-blue-300 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-lg">Email Us</h4>
                        <p className="text-blue-200">appointments@medicareplus.com</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <Clock className="w-6 h-6 text-blue-300 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-lg">Hours</h4>
                        <p className="text-blue-200">Mon-Fri: 7am-9pm | Sat-Sun: 8am-5pm</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2 p-12 bg-white text-gray-900">
                  <h3 className="text-2xl font-bold mb-6">Request an Appointment</h3>
                  <form className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-1">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={appointmentForm.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-1">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={appointmentForm.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-1">Phone *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={appointmentForm.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="service" className="block text-sm font-medium mb-1">Service *</label>
                        <select
                          id="service"
                          name="service"
                          required
                          value={appointmentForm.service}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select a service</option>
                          {services.map(service => (
                            <option key={service.id} value={service.name}>{service.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="date" className="block text-sm font-medium mb-1">Preferred Date *</label>
                        <input
                          type="date"
                                                id="date"
                      name="date"
                      required
                      value={appointmentForm.date}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">Additional Information</label>
                  <textarea
                    id="message"
                    name="message"
                    value={appointmentForm.message}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Any special requests or notes"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-lg font-bold text-lg transition duration-300 transform hover:scale-105"
                >
                  Request Appointment
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Latest News/Health Tips Section */}
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-blue-600 font-semibold mb-2 inline-block">HEALTH RESOURCES</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Latest News & Health Tips</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed with our medical insights and wellness advice.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Article 1 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="h-48 bg-gray-200 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" 
                alt="Preventive care" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              />
            </div>
            <div className="p-6">
              <span className="text-sm text-blue-600 font-medium">Preventive Care</span>
              <h3 className="text-xl font-bold text-gray-900 my-3">The Importance of Annual Check-ups</h3>
              <p className="text-gray-600 mb-4">
                Regular health screenings can detect problems before they start. Learn which tests you should consider at every age.
              </p>
              <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center group">
                Read Article
                <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Article 2 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="h-48 bg-gray-200 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1530026186672-2cd00ffc50fe?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" 
                alt="Heart health" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              />
            </div>
            <div className="p-6">
              <span className="text-sm text-blue-600 font-medium">Cardiology</span>
              <h3 className="text-xl font-bold text-gray-900 my-3">5 Signs of Heart Disease You Shouldn't Ignore</h3>
              <p className="text-gray-600 mb-4">
                Early detection of heart disease can save lives. Recognize these warning signs and when to see a specialist.
              </p>
              <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center group">
                Read Article
                <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Article 3 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="h-48 bg-gray-200 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" 
                alt="Mental health" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              />
            </div>
            <div className="p-6">
              <span className="text-sm text-blue-600 font-medium">Mental Health</span>
              <h3 className="text-xl font-bold text-gray-900 my-3">Managing Stress in Uncertain Times</h3>
              <p className="text-gray-600 mb-4">
                Expert strategies for maintaining mental wellness, from mindfulness techniques to when to seek professional help.
              </p>
              <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center group">
                Read Article
                <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <button className="inline-flex items-center px-6 py-3 border border-blue-600 text-base font-medium rounded-md shadow-sm text-blue-600 hover:bg-blue-50">
            View All Articles
            <ArrowRight className="ml-2 w-4 h-4" />
          </button>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-16 bg-gradient-to-r from-blue-800 to-blue-600 text-white">
      <div className="container mx-auto px-4 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold mb-6">Experience the MediCare+ Difference</h2>
        <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
          Join thousands of patients who trust us with their healthcare needs. Schedule your first appointment today.
        </p>
        <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg flex items-center justify-center space-x-2 transition duration-300 transform hover:scale-105">
            <Calendar className="w-5 h-5" />
            <span>Book Appointment</span>
          </button>
          <button className="bg-transparent border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-bold text-lg flex items-center justify-center space-x-2 transition duration-300 transform hover:scale-105">
            <Phone className="w-5 h-5" />
            <span>Call Now: (555) 123-4567</span>
          </button>
        </div>
      </div>
    </section>
  </main>

  {/* Footer */}
  <footer className="bg-gray-900 text-gray-400 pt-16 pb-8">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <HeartPulse className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">MediCare+</span>
          </div>
          <p className="mb-4">
            Providing exceptional healthcare services with compassion and cutting-edge technology since 2005.
          </p>
          <div className="flex space-x-4">
            <a href="#" className="hover:text-white transition">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="#" className="hover:text-white transition">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="#" className="hover:text-white transition">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="#" className="hover:text-white transition">
              <Linkedin className="w-5 h-5" />
            </a>
            <a href="#" className="hover:text-white transition">
              <Youtube className="w-5 h-5" />
            </a>
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
          <ul className="space-y-2">
            <li><a href="#" className="hover:text-white transition">Home</a></li>
            <li><a href="#" className="hover:text-white transition">Services</a></li>
            <li><a href="#" className="hover:text-white transition">Doctors</a></li>
            <li><a href="#" className="hover:text-white transition">Appointments</a></li>
            <li><a href="#" className="hover:text-white transition">Patient Portal</a></li>
            <li><a href="#" className="hover:text-white transition">Insurance</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Services</h3>
          <ul className="space-y-2">
            <li><a href="#" className="hover:text-white transition">Primary Care</a></li>
            <li><a href="#" className="hover:text-white transition">Specialty Care</a></li>
            <li><a href="#" className="hover:text-white transition">Emergency Care</a></li>
            <li><a href="#" className="hover:text-white transition">Diagnostics</a></li>
            <li><a href="#" className="hover:text-white transition">Surgery</a></li>
            <li><a href="#" className="hover:text-white transition">Wellness Programs</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Contact Us</h3>
          <ul className="space-y-3">
            <li className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <span>123 Medical Center Drive<br />Healthcare City, HC 12345</span>
            </li>
            <li className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span>(555) 123-4567</span>
            </li>
            <li className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span>info@medicareplus.com</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-gray-800 pt-8 text-center">
        <p>&copy; {new Date().getFullYear()} MediCare+ Clinic. All rights reserved. | <a href="#" className="hover:text-white transition">Privacy Policy</a> | <a href="#" className="hover:text-white transition">Terms of Service</a></p>
      </div>
    </div>
  </footer>
</div>
);
};

export default EnterpriseClinicHomepage;